import { supabase } from '../lib/supabaseClient';
import type { Database } from '../types/supabase';

type TILLedgerRow = Database['public']['Tables']['til_ledger']['Row'];

export interface BulkImportRequest {
  email: string;
  targetMinutes: number;
  currentMinutes: number;
  delta: number;
}

export interface BulkImportResult {
  email: string;
  status: 'success' | 'error' | 'no_change';
  message: string;
  deltaMinutes?: number;
}

/**
 * Supabase Balance Service
 * Handles TIL balance operations and ledger
 */
class SupabaseBalanceService {
  /**
   * Get user's TIL balance
   */
  async getUserBalance(userId: string): Promise<number> {
    const { data, error } = await supabase
      .from('users')
      .select('til_balance')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching balance:', error);
      return 0;
    }

    return (data as any)?.til_balance || 0;
  }

  /**
   * Get user's ledger history
   */
  async getUserLedger(userId: string): Promise<TILLedgerRow[]> {
    const { data, error } = await supabase
      .from('til_ledger')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching ledger:', error);
      return [];
    }

    return data || [];
  }

  /**
   * Manual balance adjustment (Admin only)
   */
  async adjustBalance(params: {
    userId: string;
    adjustmentMinutes: number;
    reason: string;
    adjustedBy: string;
    adjustedByName: string;
  }): Promise<boolean> {
    try {
      // Get current balance
      const { data: user } = await supabase
        .from('users')
        .select('til_balance, full_name')
        .eq('id', params.userId)
        .single();

      if (!user) return false;

      const previousBalance = (user as any).til_balance || 0;
      const newBalance = previousBalance + params.adjustmentMinutes;

      // Update balance
      const { error: updateError } = await supabase
        .from('users')
        .update({ til_balance: newBalance })
        .eq('id', params.userId);

      if (updateError) throw updateError;

      // Create ledger entry
      const { error: ledgerError } = await supabase
        .from('til_ledger')
        .insert({
          user_id: params.userId,
          type: 'adjustment',
          minutes: params.adjustmentMinutes,
          balance: newBalance,
          description: params.reason,
          created_by: params.adjustedBy,
        });

      if (ledgerError) throw ledgerError;

      // Log the adjustment
      await supabase.from('system_logs').insert({
        user_id: params.adjustedBy,
        user_name: params.adjustedByName,
        action: 'ADJUST_TIL_BALANCE',
        target_type: 'user',
        target_id: params.userId,
        details: {
          previousBalance,
          newBalance,
          adjustment: params.adjustmentMinutes,
          reason: params.reason,
        },
      });

      return true;
    } catch (error) {
      console.error('Error adjusting balance:', error);
      return false;
    }
  }

  /**
   * Get all balances (Admin view)
   */
  async getAllBalances(): Promise<Array<{
    userId: string;
    fullName: string;
    email: string;
    department: string;
    tilBalance: number;
  }>> {
    const { data, error } = await supabase
      .from('users')
      .select('id, full_name, email, department, til_balance')
      .eq('status', 'active')
      .order('full_name');

    if (error) {
      console.error('Error fetching balances:', error);
      return [];
    }

    return data.map((user: any) => ({
      userId: user.id,
      fullName: user.full_name,
      email: user.email,
      department: user.department || 'Unknown',
      tilBalance: user.til_balance || 0,
    }));
  }

  /**
   * Process bulk import of TIL balance adjustments
   */
  async processBulkImport(
    requests: BulkImportRequest[],
    adminUser: { uid: string; fullName: string },
    filename: string
  ): Promise<BulkImportResult[]> {
    const results: BulkImportResult[] = [];
    const importDate = new Date().toISOString().split('T')[0];

    for (const request of requests) {
      try {
        // Find user by email
        const { data: user, error: findError } = await supabase
          .from('users')
          .select('id, full_name, til_balance')
          .eq('email', request.email)
          .single();

        if (findError || !user) {
          results.push({
            email: request.email,
            status: 'error',
            message: 'User not found',
          });
          continue;
        }

        // Check if there's actually a change
        if (request.delta === 0) {
          results.push({
            email: request.email,
            status: 'no_change',
            message: 'Balance already matches target',
            deltaMinutes: 0,
          });
          continue;
        }

        // Adjust the balance
        const note = `Bulk import on ${importDate}, file: ${filename}`;
        const success = await this.adjustBalance({
          userId: user.id,
          adjustmentMinutes: request.delta,
          reason: note,
          adjustedBy: adminUser.uid,
          adjustedByName: adminUser.fullName,
        });

        if (success) {
          results.push({
            email: request.email,
            status: 'success',
            message: `Balance updated from ${request.currentMinutes} to ${request.targetMinutes} minutes (${request.delta > 0 ? '+' : ''}${request.delta} min)`,
            deltaMinutes: request.delta,
          });
        } else {
          results.push({
            email: request.email,
            status: 'error',
            message: 'Failed to update balance',
          });
        }
      } catch (error) {
        results.push({
          email: request.email,
          status: 'error',
          message: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }

    return results;
  }
}

export const supabaseBalanceService = new SupabaseBalanceService();
export default supabaseBalanceService;
